import {
  DEFAULT_LOCALE,
  mapLocaleAliasToSupportedLocale,
  SupportedLocale,
} from '@/utils/i18n/supportedLocales';

const COPY_BY_LOCALE: Record<SupportedLocale, { heading: string; tryAgain: string }> = {
  [SupportedLocale.En]: {
    heading: 'Oops, there was an error!',
    tryAgain: 'Try again',
  },
  [SupportedLocale.EnPs]: {
    heading: 'Oops, there was an error!',
    tryAgain: 'Try again',
  },
  [SupportedLocale.EsEs]: {
    heading: '¡Vaya, se produjo un error!',
    tryAgain: 'Volver a intentarlo',
  },
  [SupportedLocale.DeDe]: {
    heading: 'Hoppla, es ist ein Fehler aufgetreten!',
    tryAgain: 'Erneut versuchen',
  },
  [SupportedLocale.FrFr]: {
    heading: "Oups, une erreur s'est produite !",
    tryAgain: 'Réessayer',
  },
  [SupportedLocale.ItIt]: {
    heading: 'Ops, si è verificato un errore!',
    tryAgain: 'Riprova',
  },
  [SupportedLocale.JaJp]: {
    heading: 'エラーが発生しました',
    tryAgain: '再試行',
  },
  [SupportedLocale.KoKr]: {
    heading: '오류가 발생했습니다',
    tryAgain: '다시 시도',
  },
  [SupportedLocale.ZhCn]: {
    heading: '哎呀，出错了！',
    tryAgain: '重试',
  },
  [SupportedLocale.ZhTw]: {
    heading: '哎呀，發生錯誤！',
    tryAgain: '重試',
  },
  [SupportedLocale.CaEs]: {
    heading: "Ups, s'ha produït un error!",
    tryAgain: 'Torna-ho a provar',
  },
  [SupportedLocale.DaDk]: {
    heading: 'Ups, der opstod en fejl!',
    tryAgain: 'Prøv igen',
  },
  [SupportedLocale.NlNl]: {
    heading: 'Oeps, er is een fout opgetreden!',
    tryAgain: 'Opnieuw proberen',
  },
  [SupportedLocale.FiFi]: {
    heading: 'Hups, tapahtui virhe!',
    tryAgain: 'Yritä uudelleen',
  },
  [SupportedLocale.NbNo]: {
    heading: 'Oops, det oppstod en feil!',
    tryAgain: 'Prøv igjen',
  },
  [SupportedLocale.PlPl]: {
    heading: 'Ups, wystąpił błąd!',
    tryAgain: 'Spróbuj ponownie',
  },
  [SupportedLocale.PtPt]: {
    heading: 'Ups, ocorreu um erro!',
    tryAgain: 'Tentar novamente',
  },
  [SupportedLocale.SvSe]: {
    heading: 'Hoppsan, ett fel uppstod!',
    tryAgain: 'Försök igen',
  },
  [SupportedLocale.ArSa]: {
    heading: 'عذرًا، حدث خطأ!',
    tryAgain: 'حاول مرة أخرى',
  },
};

function resolveLocale(): SupportedLocale {
  if (typeof navigator === 'undefined') {
    return DEFAULT_LOCALE;
  }

  return (
    mapLocaleAliasToSupportedLocale(navigator.language, {
      isRtlLocaleFeatureEnabled: true,
    }) ?? DEFAULT_LOCALE
  );
}

/**
 * Dependency-free recovery UI for failures in `HeidiIntlProvider` (locale
 * resolution, OS-locale fallback, pseudolocale setup). Must not call
 * `useIntl()`, `FormattedMessage`, or design-system components that assume an
 * active intl context — this renders when the provider tree above intl is broken.
 */
export default function IntlProviderErrorFallback() {
  const locale = resolveLocale();
  const copy = COPY_BY_LOCALE[locale];

  return (
    <div
      role="alert"
      lang={locale}
      dir={locale === SupportedLocale.ArSa ? 'rtl' : 'ltr'}
      className="flex h-dvh w-dvw flex-col items-center justify-center gap-4 bg-surface-page px-6 text-center text-text-primary"
    >
      <h1 className="text-xl font-semibold">{copy.heading}</h1>
      <button
        type="button"
        className="rounded-md bg-background-button-primary px-4 py-2 text-sm font-medium text-text-primary-invert hover:bg-background-button-primary-hover"
        onClick={() => {
          location.reload();
        }}
      >
        {copy.tryAgain}
      </button>
    </div>
  );
}
